import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Video, AppWindow, Briefcase, LayoutGrid } from "lucide-react";
import {
  getProjectsApi,
  getMyProjectsApi,
  getallDemoProjectsApi,
  getDemoProjectsApi,
  getAppProjectsApi,
  getAppProjectsmyapi,
} from "../../app/api";

export default function ProjectKpiStrip() {
  const location = useLocation();
  const navigate = useNavigate();
  // Only show "all" counts on explicit admin-all pages
  const isAllView =
    location.pathname === "/projects/all" ||
    location.pathname.startsWith("/apps/list");
  const isMyView = !isAllView;
  const [counts, setCounts] = useState({ demo: 0, app: 0, simple: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCounts = async () => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const roleId = Number(user?.role_id);
      const isHRorAdmin = roleId === 2 || roleId === 1;
      const fetchAll = isHRorAdmin && isAllView;

      try {
        setLoading(true);
        const [simpleRes, appRes, demoRes] = await Promise.all([
          fetchAll ? getProjectsApi() : getMyProjectsApi(),
          fetchAll ? getAppProjectsApi() : getAppProjectsmyapi(),
          fetchAll ? getallDemoProjectsApi() : getDemoProjectsApi(),
        ]);

        const simpleCount = simpleRes?.success ? (simpleRes.data?.length || 0) : 0;
        const appCount = appRes?.success ? (appRes.data?.length || 0) : 0;
        const demoCount = demoRes?.success ? (demoRes.data?.length || 0) : 0;

        setCounts({
          simple: simpleCount,
          app: appCount,
          demo: demoCount,
        });
      } catch (err) {
        console.error("Error fetching project counts:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCounts();
  }, [location.pathname]);

  const total = counts.demo + counts.app + counts.simple;

  const scrollToList = () => {
    setTimeout(() => {
      const el = document.getElementById("project-list-section");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  const kpis = [
    {
      label: "Total Projects",
      value: total,
      subtext: "All projects combined",
      icon: LayoutGrid,
      bg: "bg-gradient-to-br from-[#c9c3d6] to-[#c9c3d6]",
      iconBg: "bg-white/20",
      iconColor: "text-[#2d2047]",
      textColor: "text-[#2d2047]",
      subtextColor: "text-[#624e8a]",
      isTotal: true,
      onClick: () => { navigate(isMyView ? "/projects/my" : "/projects/all"); scrollToList(); },
    },
    {
      label: "Demo Projects",
      value: counts.demo,
      subtext: "Interactive demos",
      icon: Video,
      bg: "bg-[#FEF3C7]",
      iconBg: "bg-amber-200/50",
      iconColor: "text-[#D97706]",
      textColor: "text-amber-900",
      subtextColor: "text-amber-600/80",
      onClick: () => { navigate(isMyView ? "/projects/my?view=demo" : "/projects/all?view=demo"); scrollToList(); },
    },
    {
      label: "App Projects",
      value: counts.app,
      subtext: "Built applications",
      icon: AppWindow,
      bg: "bg-[#E0F2FE]",
      iconBg: "bg-blue-200/50",
      iconColor: "text-[#0EA5E9]",
      textColor: "text-blue-900",
      subtextColor: "text-blue-600/80",
      onClick: () => { navigate(isAllView ? "/apps/list" : "/apps/my"); scrollToList(); },
    },
    {
      label: "Simple Projects",
      value: counts.simple,
      subtext: "General deployments",
      icon: Briefcase,
      bg: "bg-[#E0FBEA]",
      iconBg: "bg-green-200/50",
      iconColor: "text-[#22C55E]",
      textColor: "text-green-900",
      subtextColor: "text-green-600/80",
      onClick: () => { navigate(isMyView ? "/projects/my?view=simple" : "/projects/all?view=simple"); scrollToList(); },
    },
  ];

  if (loading) {
    return (
      <div className="relative z-20 mt-4 sm:mt-[-20px] px-4 sm:px-6 max-w-7xl mx-auto w-full">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-36 bg-white/10 rounded-2xl border border-white/10" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative z-20 mt-4 sm:mt-[-20px] px-4 sm:px-6 max-w-7xl mx-auto w-full">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
        {kpis.map((kpi, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.08, duration: 0.45, type: "spring", stiffness: 160 }}
            whileHover={{
              y: -8,
              scale: 1.03,
              rotateX: 4,
              rotateY: -4,
            }}
            className={`
              ${kpi.bg}
              rounded-2xl
              p-5
              relative
              overflow-hidden
              border ${kpi.isTotal ? "border-white/20" : "border-white/50"}
              shadow-[0_20px_50px_rgba(45,28,106,0.3)]
              hover:shadow-[0_30px_70px_rgba(45,28,106,0.55)]
              group
              cursor-pointer
              flex flex-col justify-between
              h-36
              active:scale-95
            `}
            style={{ transformStyle: "preserve-3d" }}
            onClick={kpi.onClick}
          >
            {/* Animated Light Sweep */}
            <div className="
              absolute -inset-10
              bg-gradient-to-r
              from-transparent
              via-white/20
              to-transparent
              opacity-0
              group-hover:opacity-100
              transition
              duration-700
              rotate-12
              pointer-events-none
            "/>

            {/* Extra glow ring for total card */}
            {kpi.isTotal && (
              <div className="absolute inset-0 rounded-2xl ring-2 ring-purple-400/30 pointer-events-none" />
            )}

            {/* Header: Icon & Pulse indicator */}
            <div className="flex items-center justify-between">
              <div className={`w-10 h-10 rounded-xl ${kpi.iconBg} flex items-center justify-center shadow-sm ring-1 ring-white/30`}>
                <kpi.icon className={kpi.iconColor} size={20} />
              </div>
              <span className={`w-2 h-2 rounded-full ${kpi.isTotal ? "bg-purple-300" : kpi.iconColor} opacity-80 animate-pulse`} />
            </div>

            {/* Labels and values */}
            <div className="mt-3">
              <p className={`${kpi.textColor} opacity-70 text-xs font-bold uppercase tracking-wider`}>
                {kpi.label}
              </p>
              <h3 className={`text-2xl font-black mt-0.5 tracking-tight ${kpi.textColor}`}>
                {kpi.value}
              </h3>
              <p className={`text-[10px] mt-0.5 font-semibold ${kpi.subtextColor}`}>
                {kpi.subtext}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
